import { useEffect, useState } from 'react';

/**
 * Detects if the device is a mobile/tablet device using capabilities + platform hints.
 */
function getIsMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check for touch capability
  const hasTouch =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // Legacy IE/Edge touch points support.
    ((navigator as { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0;

  const supportsMatchMedia = typeof window.matchMedia === 'function';
  const hasCoarsePointer = supportsMatchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  const hasNoHover = supportsMatchMedia
    ? window.matchMedia('(hover: none)').matches
    : false;

  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);

  // iPadOS can report as MacIntel while still being a touch-only device.
  const isIpadOsLikeDesktopUa = navigator.platform === 'MacIntel' && hasTouch;

  return (
    (hasTouch && (hasCoarsePointer || hasNoHover)) ||
    isMobileUA ||
    isIpadOsLikeDesktopUa
  );
}

function isMobileViewport(breakpoint: number): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
  }

  return window.innerWidth < breakpoint;
}

/**
 * Hook to detect responsive breakpoints
 * @param breakpoint - The pixel width to use as mobile/desktop threshold (default: 768)
 * @returns Object with isMobile, isDesktop, and isMobileDevice booleans
 */
export function useResponsive(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => isMobileViewport(breakpoint));

  // This value won't change during session - device type is static
  const [isMobileDevice] = useState(getIsMobileDevice);

  useEffect(() => {
    const query = `(max-width: ${breakpoint - 1}px)`;
    const mediaQueryList =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(query)
        : undefined;

    const update = () => {
      if (mediaQueryList) {
        setIsMobile(mediaQueryList.matches);
        return;
      }

      setIsMobile(window.innerWidth < breakpoint);
    };

    update();

    if (mediaQueryList) {
      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', update);
      } else {
        mediaQueryList.addListener(update);
      }
    }

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      if (mediaQueryList) {
        if (typeof mediaQueryList.removeEventListener === 'function') {
          mediaQueryList.removeEventListener('change', update);
        } else {
          mediaQueryList.removeListener(update);
        }
      }

      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, [breakpoint]);

  // Keep mobile-optimized UI on actual mobile devices even in landscape.
  const prefersMobileLayout = isMobile || isMobileDevice;

  return {
    isMobile,
    isDesktop: !isMobile,
    isMobileDevice, // true only on actual mobile/tablet devices
    prefersMobileLayout,
    breakpoint,
  };
}
